import { MAX_COUNT, RecurrenceError, WEEKDAYS, } from './types.js';
const SUPPORTED_KEYS = new Set([
    'FREQ',
    'INTERVAL',
    'BYDAY',
    'BYMONTHDAY',
    'BYMONTH',
    'WKST',
    'COUNT',
    'UNTIL',
]);
const KNOWN_UNSUPPORTED = new Set([
    'BYSETPOS',
    'BYHOUR',
    'BYMINUTE',
    'BYSECOND',
    'BYYEARDAY',
    'BYWEEKNO',
    'RSCALE',
    'SKIP',
]);
const FREQS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
const BYDAY_RE = /^([+-]?\d{1,2})?(MO|TU|WE|TH|FR|SA|SU)$/;
const INT_RE = /^[+-]?\d{1,4}$/;
function fail(code, message) {
    throw new RecurrenceError(code, message);
}
function parseInt10(value, what) {
    if (!INT_RE.test(value))
        fail('syntax', `${what}: geen geheel getal (${value})`);
    return Number(value);
}
function isWeekday(v) {
    return WEEKDAYS.indexOf(v) >= 0;
}
/** Parst `FREQ=WEEKLY;BYDAY=TU,TH` (met of zonder `RRULE:`-prefix). */
export function parseRRule(text) {
    const body = text.trim().replace(/^RRULE:/i, '');
    if (body.length === 0)
        fail('syntax', 'lege regel');
    if (body.length > 300)
        fail('syntax', 'regel te lang');
    const seen = new Set();
    const rule = { freq: 'DAILY' };
    const out = { rule };
    let hasFreq = false;
    for (const part of body.split(';')) {
        if (part === '')
            continue;
        const eq = part.indexOf('=');
        if (eq <= 0)
            fail('syntax', `onderdeel zonder '=': ${part}`);
        const key = part.slice(0, eq).toUpperCase();
        const value = part.slice(eq + 1).toUpperCase();
        if (seen.has(key))
            fail('syntax', `${key} komt twee keer voor`);
        seen.add(key);
        if (KNOWN_UNSUPPORTED.has(key))
            fail('unsupported', `${key} wordt niet ondersteund`);
        if (!SUPPORTED_KEYS.has(key))
            fail('unsupported', `onbekend onderdeel ${key}`);
        if (value === '')
            fail('syntax', `${key} is leeg`);
        switch (key) {
            case 'FREQ': {
                if (!FREQS.includes(value)) {
                    fail('unsupported', `FREQ=${value} wordt niet ondersteund`);
                }
                rule.freq = value;
                hasFreq = true;
                break;
            }
            case 'INTERVAL':
                rule.interval = parseInt10(value, 'INTERVAL');
                break;
            case 'BYDAY':
                rule.byDay = value.split(',').map((item) => {
                    const m = BYDAY_RE.exec(item);
                    if (!m || !m[2] || !isWeekday(m[2]))
                        fail('syntax', `BYDAY-waarde ${item}`);
                    const weekday = m[2];
                    return m[1] !== undefined ? { weekday, ordinal: Number(m[1]) } : { weekday };
                });
                break;
            case 'BYMONTHDAY':
                rule.byMonthDay = value.split(',').map((v) => parseInt10(v, 'BYMONTHDAY'));
                break;
            case 'BYMONTH':
                rule.byMonth = value.split(',').map((v) => parseInt10(v, 'BYMONTH'));
                break;
            case 'WKST':
                if (!isWeekday(value))
                    fail('syntax', `WKST=${value}`);
                rule.weekStart = value;
                break;
            case 'COUNT':
                out.count = parseInt10(value, 'COUNT');
                break;
            case 'UNTIL':
                parseUntil(value, out);
                break;
        }
    }
    if (!hasFreq)
        fail('syntax', 'FREQ ontbreekt');
    validateRule(rule);
    if (out.count !== undefined && (out.until !== undefined || out.untilUtc !== undefined)) {
        fail('count_and_until', 'COUNT en UNTIL sluiten elkaar uit');
    }
    if (out.count !== undefined)
        validateCount(out.count);
    return out;
}
function parseUntil(value, out) {
    const d = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
    if (d) {
        out.until = `${d[1]}-${d[2]}-${d[3]}`;
        return;
    }
    const dt = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/.exec(value);
    if (!dt)
        fail('syntax', `UNTIL=${value}`);
    const local = `${dt[1]}-${dt[2]}-${dt[3]}T${dt[4]}:${dt[5]}:${dt[6]}`;
    if (dt[7] === 'Z')
        out.untilUtc = `${local}.000Z`;
    else
        out.until = local;
}
export function validateCount(count) {
    if (!Number.isInteger(count) || count < 1 || count > MAX_COUNT) {
        fail('invalid_value', `COUNT moet tussen 1 en ${MAX_COUNT} liggen`);
    }
}
/** Controleert de subset-regels; gooit RecurrenceError. */
export function validateRule(rule) {
    if (!FREQS.includes(rule.freq)) {
        fail('unsupported', `FREQ=${String(rule.freq)} wordt niet ondersteund`);
    }
    const interval = rule.interval ?? 1;
    if (!Number.isInteger(interval) || interval < 1 || interval > 999) {
        fail('invalid_value', 'INTERVAL moet tussen 1 en 999 liggen');
    }
    if (rule.byDay) {
        if (rule.byDay.length === 0)
            fail('invalid_value', 'BYDAY is leeg');
        for (const d of rule.byDay) {
            if (!isWeekday(d.weekday))
                fail('invalid_value', `BYDAY-weekdag ${String(d.weekday)}`);
            if (d.ordinal !== undefined) {
                if (rule.freq === 'DAILY' || rule.freq === 'WEEKLY') {
                    fail('invalid_value', `BYDAY met ordinaal kan niet bij ${rule.freq}`);
                }
                if (!Number.isInteger(d.ordinal) || d.ordinal === 0 || Math.abs(d.ordinal) > 5) {
                    fail('invalid_value', 'BYDAY-ordinaal moet 1..5 of -1..-5 zijn');
                }
            }
        }
        if (rule.freq === 'YEARLY' && !rule.byMonth) {
            fail('unsupported', 'BYDAY bij YEARLY vereist BYMONTH');
        }
    }
    if (rule.byMonthDay) {
        if (rule.freq === 'DAILY' || rule.freq === 'WEEKLY') {
            fail('unsupported', `BYMONTHDAY kan niet bij ${rule.freq}`);
        }
        if (rule.byMonthDay.length === 0)
            fail('invalid_value', 'BYMONTHDAY is leeg');
        for (const v of rule.byMonthDay) {
            if (!Number.isInteger(v) || v === 0 || Math.abs(v) > 31) {
                fail('invalid_value', 'BYMONTHDAY moet 1..31 of -1..-31 zijn');
            }
        }
    }
    if (rule.byMonth) {
        if (rule.freq !== 'YEARLY')
            fail('unsupported', 'BYMONTH kan alleen bij YEARLY');
        if (rule.byMonth.length === 0)
            fail('invalid_value', 'BYMONTH is leeg');
        for (const m of rule.byMonth) {
            if (!Number.isInteger(m) || m < 1 || m > 12)
                fail('invalid_value', 'BYMONTH moet 1..12 zijn');
        }
    }
}
function byDayText(d) {
    return `${d.ordinal !== undefined ? String(d.ordinal) : ''}${d.weekday}`;
}
/**
 * Canonieke tekst: vaste volgorde, INTERVAL alleen als > 1, WKST alleen als afwijkend van MO.
 * Twee gelijke regels geven dus dezelfde tekst (gebruikt voor vergelijken en in de database).
 */
export function formatRRule(rule, options = {}) {
    validateRule(rule);
    const parts = [`FREQ=${rule.freq}`];
    if ((rule.interval ?? 1) > 1)
        parts.push(`INTERVAL=${String(rule.interval)}`);
    if (rule.byMonth && rule.byMonth.length > 0) {
        parts.push(`BYMONTH=${[...rule.byMonth].sort((a, b) => a - b).join(',')}`);
    }
    if (rule.byMonthDay && rule.byMonthDay.length > 0) {
        parts.push(`BYMONTHDAY=${sortMonthDays(rule.byMonthDay).join(',')}`);
    }
    if (rule.byDay && rule.byDay.length > 0) {
        parts.push(`BYDAY=${sortByDay(rule.byDay).map(byDayText).join(',')}`);
    }
    if (rule.weekStart && rule.weekStart !== 'MO')
        parts.push(`WKST=${rule.weekStart}`);
    if (options.count !== undefined && options.count !== null) {
        parts.push(`COUNT=${String(options.count)}`);
    }
    if (options.until !== undefined && options.until !== null)
        parts.push(`UNTIL=${options.until}`);
    return parts.join(';');
}
function sortMonthDays(days) {
    // Positief oplopend, daarna negatief (-1 als laatste).
    return [...new Set(days)].sort((a, b) => {
        if (a > 0 && b < 0)
            return -1;
        if (a < 0 && b > 0)
            return 1;
        return a > 0 ? a - b : b - a;
    });
}
export function sortByDay(days) {
    const seen = new Set();
    const out = [];
    for (const d of days) {
        const key = byDayText(d);
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(d);
    }
    return out.sort((a, b) => WEEKDAYS.indexOf(a.weekday) - WEEKDAYS.indexOf(b.weekday) ||
        (a.ordinal ?? 0) - (b.ordinal ?? 0));
}
/** `2026-06-30` → `20260630` (UNTIL voor hele-dag-series, RFC 5545 DATE-vorm). */
export function formatUntilDate(date) {
    return date.replace(/-/g, '');
}
/** ISO-instant → `20260630T215900Z` (UNTIL bij een DTSTART met TZID moet UTC zijn). */
export function formatUntilUtc(isoInstant) {
    const d = new Date(isoInstant);
    const p = (n) => (n < 10 ? `0${n}` : String(n));
    return (`${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
        `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`);
}
/** Twee regels gelijk (na canonicalisatie)? */
export function sameRule(a, b) {
    if (a === null || b === null)
        return a === b;
    return formatRRule(a) === formatRRule(b);
}
//# sourceMappingURL=rrule.js.map